import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { CircleHelpIcon, PlusIcon } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as z from 'zod';

import {
  type FulfillmentProfileDTO,
  type OrderDTO,
  type ShippingProfileDTO,
  type UpdateOrderDTO,
} from '@invenco/sales-interface/interfaces';

import { Button } from '@/components/core/button';
import {
  ComboboxContent,
  ComboboxEmpty,
  ComboboxItem,
  ComboboxList,
} from '@/components/core/combobox';
import { DialogBody, DialogHeader, DialogTitle } from '@/components/core/dialog';
import { Separator } from '@/components/core/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/core/tooltip';
import { ComboboxFormField } from '@/components/forms/combobox-form-field';
import { FormDialogContent } from '@/components/forms/form-dialog';
import { NumberFormField } from '@/components/forms/number-form-field';
import { SwitchFormField } from '@/components/forms/switch-form-field';
import { TextFormField } from '@/components/forms/text-form-field';
import { TextareaFormField } from '@/components/forms/textarea-form-field';
import { SUPPORTED_CURRENCIES } from '@/constants';
import { useGateways } from '@/gateways/GatewayProvider';
import type { Result } from '@/shared/helpers/Result';

import {
  CustomerFields,
  addressesMatch,
  customerFieldsDefaultValues,
  customerFieldsSchema,
  getAddressValues,
  type CustomerFieldsValues,
} from '../customer-fields';

const DEFAULT_FULFILLMENT_PROFILE_ID = '__default__';

const orderEditSchema = customerFieldsSchema.and(
  z.object({
    shippingProfileName: z.string().trim().min(1, 'Select a shipping profile'),
    subtotalShipping: z
      .number('Enter a shipping charge')
      .nonnegative('Enter a valid shipping charge'),
    channelName: z.string().trim(),
    currency: z.enum(SUPPORTED_CURRENCIES),
    allowPartialFulfillments: z.boolean(),
    fulfillmentProfileId: z.string(),
    reference: z.string().trim(),
    notes: z.string().trim(),
  }),
);

type OrderEditFormValues = z.input<typeof orderEditSchema>;
type OrderEditSubmitValues = z.output<typeof orderEditSchema>;

type ComboboxOption = {
  value: string;
  label: string;
};

type Props = {
  order: OrderDTO;
  onSubmit: (patch: UpdateOrderDTO) => Promise<Result<OrderDTO>>;
};

function SectionHeading({ children }: { children: string }) {
  return <h3 className="font-heading text-sm font-semibold">{children}</h3>;
}

function ComboboxOptions({
  emptyMessage,
  portalContainer,
}: {
  emptyMessage: string;
  portalContainer?: HTMLElement;
}) {
  return (
    <ComboboxContent portalContainer={portalContainer}>
      <ComboboxEmpty>{emptyMessage}</ComboboxEmpty>
      <ComboboxList>
        {(option: ComboboxOption) => (
          <ComboboxItem key={option.value} value={option.value}>
            {option.label}
          </ComboboxItem>
        )}
      </ComboboxList>
    </ComboboxContent>
  );
}

function getCustomerValues(order: OrderDTO): CustomerFieldsValues {
  return {
    ...customerFieldsDefaultValues,
    customerId: order.customerId ?? undefined,
    customerName: order.customerName ?? '',
    email: order.email ?? '',
    phone: order.phone ?? '',
    shippingAddress: getAddressValues(order.shippingAddress),
    billingAddressMatchesShippingAddress: addressesMatch(
      order.shippingAddress,
      order.billingAddress,
    ),
    billingAddress: getAddressValues(order.billingAddress),
  };
}

export function OrderEditDialogContent({ order, onSubmit }: Props) {
  const { salesGateway } = useGateways();
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const [isAddingReference, setIsAddingReference] = useState(false);
  const [isAddingNotes, setIsAddingNotes] = useState(false);

  const { data: channelData } = useQuery({
    queryKey: ['channels'],
    queryFn: ({ signal }) => salesGateway.getChannels(undefined, { signal }),
  });
  const { data: shippingProfileData } = useQuery({
    queryKey: ['shippingProfiles'],
    queryFn: ({ signal }) => salesGateway.getShippingProfiles(undefined, { signal }),
  });
  const { data: fulfillmentProfiles = [] } = useQuery({
    queryKey: ['fulfillmentProfiles'],
    queryFn: ({ signal }) => salesGateway.getFulfillmentProfiles(undefined, { signal }),
    select: ({ items }) => items,
  });

  const selectedChannel = useMemo(
    () =>
      channelData?.items.find(
        (channel) => channel.id === order.channelId || channel.name === order.channelName,
      ),
    [channelData?.items, order.channelId, order.channelName],
  );
  const selectedChannelId = selectedChannel?.id ?? order.channelId;
  const shippingProfileOptions = useMemo<ShippingProfileDTO[]>(() => {
    const profiles = shippingProfileData?.items ?? [];
    return selectedChannelId
      ? profiles.filter((profile) => profile.channelId === selectedChannelId || !profile.channelId)
      : profiles;
  }, [selectedChannelId, shippingProfileData?.items]);
  const fulfillmentProfileOptions = useMemo<ComboboxOption[]>(() => {
    const currentProfile = fulfillmentProfiles.find(
      (profile) => profile.id === order.fulfillmentProfileId,
    );
    const filteredProfiles = selectedChannel
      ? fulfillmentProfiles.filter((profile) => {
          const locationIds = new Set([
            ...profile.fulfillmentLocationIds,
            profile.fallbackLocationId,
          ]);
          return selectedChannel.fulfillmentLocationIds.some((locationId) =>
            locationIds.has(locationId),
          );
        })
      : fulfillmentProfiles;
    const profileOptions = currentProfile
      ? [
          ...filteredProfiles,
          ...(filteredProfiles.some((profile) => profile.id === currentProfile.id)
            ? []
            : [currentProfile]),
        ]
      : filteredProfiles;

    return [
      { value: DEFAULT_FULFILLMENT_PROFILE_ID, label: 'Default' },
      ...profileOptions.map((profile) => ({ value: profile.id, label: profile.name })),
    ];
  }, [fulfillmentProfiles, order.fulfillmentProfileId, selectedChannel]);

  const defaultValues = useMemo<OrderEditFormValues>(
    () => ({
      ...getCustomerValues(order),
      shippingProfileName: order.shippingProfileName ?? '',
      subtotalShipping: order.subtotalShipping ?? 0,
      channelName: order.channelName ?? 'Default',
      currency: order.currency,
      allowPartialFulfillments: order.allowPartialFulfillments ?? false,
      fulfillmentProfileId: order.fulfillmentProfileId ?? DEFAULT_FULFILLMENT_PROFILE_ID,
      reference: order.reference ?? '',
      notes: order.notes ?? '',
    }),
    [order],
  );

  const form = useForm<OrderEditFormValues, unknown, OrderEditSubmitValues>({
    resolver: zodResolver(orderEditSchema),
    defaultValues,
  });

  useEffect(() => {
    form.reset(defaultValues);
    setIsAddingReference(Boolean(defaultValues.reference));
    setIsAddingNotes(Boolean(defaultValues.notes));
  }, [defaultValues, form]);

  const isShowingReference = isAddingReference || Boolean(order.reference);
  const isShowingNotes = isAddingNotes || Boolean(order.notes);

  return (
    <FormDialogContent
      form={form}
      side="right"
      className="sm:max-w-xl lg:max-w-2xl"
      aria-describedby={undefined}
      onSubmit={(data) => {
        const selectedProfile = fulfillmentProfiles.find(
          (profile: FulfillmentProfileDTO) => profile.id === data.fulfillmentProfileId,
        );
        const isDefaultProfile = data.fulfillmentProfileId === DEFAULT_FULFILLMENT_PROFILE_ID;
        const isExistingProfile = data.fulfillmentProfileId === order.fulfillmentProfileId;

        return onSubmit({
          customerName: data.customerName,
          email: data.email,
          phone: data.phone,
          shippingAddress: data.shippingAddress,
          billingAddress: data.billingAddress,
          shippingProfileName: data.shippingProfileName,
          subtotalShipping: data.subtotalShipping,
          currency: data.currency,
          allowPartialFulfillments: data.allowPartialFulfillments,
          fulfillmentProfileId: isDefaultProfile
            ? null
            : (selectedProfile?.id ?? data.fulfillmentProfileId),
          fulfillmentProfileName: isDefaultProfile
            ? null
            : (selectedProfile?.name ?? (isExistingProfile ? order.fulfillmentProfileName : null)),
          reference: data.reference || null,
          notes: data.notes || null,
        });
      }}
    >
      <DialogHeader>
        <DialogTitle>Edit {order.name}</DialogTitle>
      </DialogHeader>
      <DialogBody ref={setPortalContainer} className="gap-5">
        <section className="space-y-4">
          <SectionHeading>Customer</SectionHeading>
          <CustomerFields />
        </section>

        <Separator />

        <section className="space-y-4">
          <SectionHeading>Shipping</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <ComboboxFormField
              name="shippingProfileName"
              label="Shipping profile"
              items={shippingProfileOptions.map((profile) => ({
                value: profile.name,
                label: profile.name,
              }))}
              aria-label="Shipping profile"
            >
              <ComboboxOptions
                emptyMessage="No shipping profiles found"
                portalContainer={portalContainer ?? undefined}
              />
            </ComboboxFormField>
            <NumberFormField
              name="subtotalShipping"
              label="Shipping charge"
              aria-label="Shipping charge"
              min={0}
              step="any"
            />
          </div>
        </section>

        <Separator />

        <section className="space-y-4">
          <SectionHeading>Properties</SectionHeading>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextFormField name="channelName" label="Channel" aria-label="Channel" disabled />
            <ComboboxFormField
              name="currency"
              label="Currency"
              items={SUPPORTED_CURRENCIES.map((currency) => ({ value: currency, label: currency }))}
              aria-label="Currency"
            >
              <ComboboxOptions
                emptyMessage="No currencies found"
                portalContainer={portalContainer ?? undefined}
              />
            </ComboboxFormField>
          </div>
          <SwitchFormField
            name="allowPartialFulfillments"
            label={
              <span className="inline-flex items-center gap-1.5">
                Partial fulfillment
                <Tooltip>
                  <TooltipTrigger asChild>
                    <CircleHelpIcon
                      tabIndex={0}
                      aria-label="Partial fulfillment help"
                      className="size-4 text-muted-foreground"
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    Allows a fulfillment to be created with only partially available inventory. This
                    will automatically toggle off when the next fulfillment is created upon order
                    release.
                  </TooltipContent>
                </Tooltip>
              </span>
            }
            aria-label="Partial fulfillment"
          />
          <ComboboxFormField
            name="fulfillmentProfileId"
            label="Fulfillment profile"
            items={fulfillmentProfileOptions}
            itemToStringLabel={(value) =>
              fulfillmentProfileOptions.find((option) => option.value === value)?.label ?? ''
            }
            aria-label="Fulfillment profile"
          >
            <ComboboxOptions
              emptyMessage="No fulfillment profiles found"
              portalContainer={portalContainer ?? undefined}
            />
          </ComboboxFormField>
        </section>

        <Separator />

        <section className="space-y-4">
          {!isShowingReference ? (
            <Button
              type="button"
              variant="ghost"
              className="-mx-2 w-[calc(100%+--spacing(4))] justify-start px-2"
              onClick={() => setIsAddingReference(true)}
            >
              <PlusIcon />
              Add reference
            </Button>
          ) : null}
          {isShowingReference ? (
            <>
              <SectionHeading>Reference</SectionHeading>
              <TextareaFormField name="reference" aria-label="Reference" rows={3} />
            </>
          ) : null}
        </section>

        <Separator />

        <section className="space-y-4">
          {!isShowingNotes ? (
            <Button
              type="button"
              variant="ghost"
              className="-mx-2 w-[calc(100%+--spacing(4))] justify-start px-2"
              onClick={() => setIsAddingNotes(true)}
            >
              <PlusIcon />
              Add notes
            </Button>
          ) : null}
          {isShowingNotes ? (
            <>
              <SectionHeading>Notes</SectionHeading>
              <TextareaFormField name="notes" aria-label="Notes" rows={4} />
            </>
          ) : null}
        </section>
      </DialogBody>
    </FormDialogContent>
  );
}
